<script context="module" lang="ts">
	import Section from '$lib/components/layout/Section.svelte';
	import type { SectionConfig } from '$lib/util/pagebuilder';
	import { gql } from 'graphql-request';
	import api from '$lib/api/ecom/shopify';
	import { page } from '$app/stores';
	import { getVariantName } from '$lib/util/ecom/util';
	import { slide } from 'svelte/transition';

	export async function load(data: Props, params: any, additionalLoadData: any = {}) {
		const productsList = JSON.parse(data.product);

		let products = (
			await shopify.getProductsById(
				productsList
					?.map((item) => item.selectedProduct?.node?.id)
					.filter((item): item is string => !!item) ?? [],
				additionalLoadData.data.location,
				params.lang
			)
		).nodes;

		return {
			products
		};
	}

	export const config: SectionConfig = {
		collection: 'section_subscription',
		availableFor: ['pages', 'special-pages'],
		query: gql`
        internal_name
        translations {
            languages_code {
                code
            }
            title
            text
            subtitle
        }
        link {
            ...Link
        }
        product
    
    `,
		load
	};

	// optional: define a type for the props (note: injectTranslations is applied)
	type Props = {
		media: any;
		title: string;
		text: string;
		subtitle: string;
		link: any;
		product: any;
	};
</script>

<script lang="ts">
	import Wysiwyg from '$lib/components/inputs/WYSIWYG.svelte';
	import DirectusLink from '$lib/components/navigation/DirectusLink.svelte';
	import TextWithIcon from '$lib/components/util/TextWithIcon.svelte';
	import Button from '$lib/components/util/Button.svelte';
	import ProductCard from '$lib/components/ecom/ProductCard.svelte';
	import GlobalMedia from '$lib/components/media/GlobalMedia.svelte';
	import shopify from '$lib/api/ecom/shopify';
	import Dropdown from '$lib/components/inputs/Dropdown.svelte';
	import { onMount } from 'svelte';
	import { getText } from '$lib/util/translations';
	import { addToCart, getSubscriptionData } from '$lib/stores/shopStore';
	import Text from '$lib/components/Text.svelte';
	import QuantitySelector from '$lib/components/ecom/QuantitySelector.svelte';
	import { formatPrice } from '$lib/util/ecom/format';
	import { injectDiscounts } from '$lib/util/ecom/discounts';

	export let data: Props; // the components gets all data via this prop (additionally each top level prop is directly provided to the component)
	export let additionalData: Awaited<ReturnType<typeof load>>;

	let sellingPlans: any[] = [];
	let subscriptionProducts: any[] = [];

	let selectedProductIdx = -1;
	let selectedVariantIdx = -1;
	let selectedSellingPlanIdx = -1;
	let selectedAmountIdx = -1;
	let selectedQuantity = 1;

	/*
	async function getSubscriptionData(location: string, lang: string) {
		const res = await fetch('/api/get-selling-plans');
		const subscriptionData = await res.json();

		const productIds: any[] = [];

		if (subscriptionData?.subscriptions_rules?.length === 0) {
			return;
		}
		const rule = subscriptionData.subscription_rules[0];

		for (const product of rule.product_variants) {
			const id = 'gid://shopify/Product/' + product.product_id;
			if (!productIds.includes(id)) {
				productIds.push(id);
			}
		}

		const productData = await api.getProductsById(productIds, location, lang);

		return {
			sellingPlans: rule.selling_plans,
			subscriptionProducts: productData?.nodes ?? []
		};
	}
	*/

	onMount(async () => {
		const data = await getSubscriptionData($page.data.location, $page.params.lang);

		sellingPlans = data.sellingPlans;
		subscriptionProducts = data.subscriptionProducts;
	});

	function previewImage() {
		const product = subscriptionProducts[selectedProductIdx];
		if (!product.variants.edges[selectedVariantIdx]?.image?.url) {
			return product && product.media && product.media.edges.length > 0
				? product.media.edges[0].node.previewImage.url
				: '';
		}

		return product.variants.edges[selectedVariantIdx]?.image?.url;
	}

	function addSubscription() {
		if (
			selectedProductIdx >= 0 &&
			selectedVariantIdx >= 0 &&
			selectedSellingPlanIdx >= 0 &&
			selectedQuantity >= 1
		) {
			const variantId =
				subscriptionProducts[selectedProductIdx].variants.edges[selectedVariantIdx]?.node?.id;

			addToCart(
				variantId,
				selectedQuantity,
				discountedPrice?.amount,
				discountedPrice?.currencyCode,
				subscriptionProducts[selectedProductIdx].title,
				previewImage(),
				'gid://shopify/SellingPlan/' + sellingPlans[selectedSellingPlanIdx].selling_plan_id
			);
		}
	}

	$: selectedProduct = subscriptionProducts[selectedProductIdx];
	$: selectedSellingPlan = sellingPlans[selectedSellingPlanIdx];

	$: basePrice = selectedProduct?.variants.edges[selectedVariantIdx]?.node?.priceV2;
	$: discount =
		selectedSellingPlan?.pricing_policy_fixed_adjustment_type === 'PERCENTAGE'
			? Number(selectedSellingPlan?.pricing_policy_fixed_adjustment_value) / 100
			: 0;
	$: discountedPrice = {
		...basePrice,
		amount: Math.round(basePrice?.amount * (1 - discount) * 100) / 100
	};
	$: showPrice = selectedProductIdx >= 0 && selectedVariantIdx >= 0 && selectedSellingPlanIdx >= 0;
</script>

<Section>
	<div class="grid">
		<div class="col1">
			<div class="icon">
				<GlobalMedia key="logo" fit="cover" />
			</div>
			<div class="divider1" />
			<div class="content">
				<div class="title">
					<div class="icon-mobile">
						<GlobalMedia key="logo" fit="cover" />
					</div>
					{data?.title}
				</div>
				<div class="divider" />
				<div class="text"><Wysiwyg text={data.text} /></div>
				<div class="subtitle">{data.subtitle}</div>
				<!-- replace with subscritpion values -->
				<div class="subscripion-settings">
					{#if selectedProductIdx >= 0}
						<div class="label">1. <Text key="which_product" /></div>
					{:else}
						<div class="label"><Text key="which_product" /></div>
					{/if}

					<Dropdown bind:value={selectedProductIdx} type="normal">
						<option disabled selected value={-1}
							>{getText($page.data.globalTexts, 'ecom_which_product').text}</option
						>
						{#each subscriptionProducts as product, idx}
							<option value={idx}>{product?.title}</option>
						{/each}
					</Dropdown>

					{#if selectedProductIdx >= 0}
						<div transition:slide>
							<div class="label">2. <Text key="which_size" /></div>
							<Dropdown bind:value={selectedVariantIdx} type="normal">
								<option disabled selected value={-1}
									>{getText($page.data.globalTexts, 'ecom_which_size').text}</option
								>
								{#each subscriptionProducts[selectedProductIdx].variants.edges as variant, idx}
									<option value={idx}>{getVariantName(variant.node)}</option>
								{/each}
							</Dropdown>
						</div>
					{/if}
					{#if selectedVariantIdx >= 0}
						<div transition:slide>
							<div class="label">3. <Text key="how_often" /></div>
							<Dropdown bind:value={selectedSellingPlanIdx} type="normal">
								<option disabled selected value={-1}
									>{getText($page.data.globalTexts, 'ecom_which_interval').text}</option
								>
								{#each sellingPlans as sellingPlan, idx}
									<option value={idx}>
										{sellingPlan?.name}
										<!-- {sellingPlan.delivery_interval_count}
										{getText(
											$page.data.globalTexts,
											'ecom_' +
												sellingPlan.delivery_interval +
												(sellingPlan.delivery_interval_count > 1 ? 's' : '')
										).text} -->
									</option>
								{/each}
							</Dropdown>
						</div>
					{/if}
					{#if selectedSellingPlanIdx >= 0}
						<div transition:slide>
							<div class="label">4. <Text key="how_many" /></div>
							<div class="quanitity-selector">
								<QuantitySelector bind:value={selectedQuantity} max={10} />
							</div>
						</div>
					{/if}
					{#if showPrice}
						<div class="price-title">
							<Text key="ecom_exclusive_price" />
						</div>
						<div class="price-wrapper">
							<div class="price">
								{formatPrice(
									injectDiscounts(discountedPrice?.amount * selectedQuantity),
									discountedPrice?.currencyCode,
									$page.params.lang
								)}
							</div>
							<div class="compare-price">
								{formatPrice(
									injectDiscounts(basePrice?.amount * selectedQuantity),
									basePrice?.currencyCode,
									$page.params.lang
								)}
							</div>
						</div>
					{/if}
				</div>
				<div class="button-wrapper">
					<Button on:click={addSubscription} textKey="subscribe_now" style="yellow" />
				</div>
				<div class="divider" />
				<div class="link">
					<DirectusLink {...data.link}><TextWithIcon {...data.link} /></DirectusLink>
				</div>
			</div>
		</div>
		<div class="col2">
			<div class="product-wrapper">
				{#each additionalData.products ?? [] as product}
					<ProductCard {product} isSubscription />
				{/each}
			</div>
		</div>
	</div>
</Section>

<style lang="scss">
	.grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: v(content-gap-sm);
		height: 100%;

		@media (max-width: 1200px) {
			grid-template-columns: repeat(1, minmax(0, 1fr));
		}
	}
	.icon {
		padding-inline-end: v(content-gap);
		display: flex;
		height: 100%;
		justify-content: center;
		align-items: center;
		:global(img) {
			height: auto;
			max-width: 100px;
		}
		@media (max-width: 1100px) {
			display: none;
		}
	}
	.title {
		@include font(h3);
		display: flex;
		gap: v(content-gap-xs);
		align-items: center;
		max-width: 20ch;
		@media (max-width: 1100px) {
			max-width: 100%;
		}
		.icon-mobile {
			display: none;
			@media (max-width: 1100px) {
				display: flex;
				width: 150px;
			}
		}
	}
	.text {
		@include font(text);
		max-width: 50ch;
		@media (max-width: 1100px) {
			max-width: 100%;
		}
	}
	.subtitle {
		padding: v(content-padding) 0;
		@include font(h6);
	}
	.col1 {
		display: flex;
		background: color(yellow_02);
		padding: v(content-gap-xl);
		@include media(phone) {
			padding: v(content-gap);
		}
	}
	.content {
		padding-inline-start: v(content-gap);
		width: 100%;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		align-items: flex-start;
		@media (max-width: 1100px) {
			border-inline-start: none;
			padding-inline-start: 0;
		}
	}
	.divider {
		background: color(black);
		width: 100%;
		height: 1px;
		margin: v(content-gap-sm) 0;
	}
	.divider1 {
		background: color(black);
		width: 1px;
		height: auto;
		@media (max-width: 1100px) {
			display: none;
		}
	}
	.col2 {
		display: flex;
		gap: v(content-gap-xs);
	}
	.subscripion-settings {
		display: flex;
		flex-direction: column;
		// gap: v(content-gap-xs);
		flex-wrap: wrap;
		:global(.dropdown) {
			margin-bottom: v(content-gap-sm);

			@media (max-width: 500px) {
				height: 2.5rem;
			}
		}
	}
	.product-wrapper {
		display: flex;
		gap: v(content-gap-sm);
		@media (max-width: 600px) {
			flex-direction: column;
		}
	}

	.button-wrapper {
		:global(button) {
			width: 12rem !important;
			text-align: center !important;
			white-space: nowrap !important;
			display: flex;
			justify-content: center;
		}
		:global(.text) {
			text-align: center;
		}

		@media (max-width: 500px) {
			:global(button) {
				width: 100%;
				:global(.text) {
					width: 100%;
					text-align: center;
				}
			}
		}
	}
	.label {
		@include font(h6);
		display: flex;
		gap: v(content-gap-xs);
		margin-bottom: v(content-gap-2xs);
	}

	.quanitity-selector {
		margin-bottom: v(content-gap-sm);
		:global(.quantitiy-selector) {
			border-radius: 4px !important;
			border: 0 !important;
			width: 12rem !important;
		}
		:global(.textfield) {
			@include font(text);
		}
		:global(button) {
			display: inline-flex;
			align-items: center;
			justify-content: center;
		}
		:global(.svg) {
			flex-shrink: 0;
		}
	}

	.option-row {
		display: flex;
		justify-content: space-between;
	}

	.price-title {
		@include font(h6);
		margin-bottom: v(content-padding-xs);
	}

	.price-wrapper {
		display: flex;
		flex-direction: column;
		justify-content: center;
		text-wrap: nowrap;
		flex: 1;
		margin-bottom: v(content-padding-sm);

		.price {
			@include font(h4);
		}
		.compare-price {
			@include font(text);
			text-decoration: line-through;
		}
	}
</style>
